import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { Info } from 'lucide-react';

export interface DestinationSettings {
  message: string;
  description: string;
  transferMode: 'rolling-history' | 'swap-system-message-in-history' | 'delete-history';
}

interface AssistantDestinationSettingsProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  settings: DestinationSettings;
  onSettingsChange: (settings: DestinationSettings) => void;
  destinationName: string;
}

const transferModeInfo = {
  'rolling-history': 'Keeps the entire conversation history and appends the new assistant\'s system message on transfer.',
  'swap-system-message-in-history': 'Replaces the original system message with the new assistant\'s system message on transfer.',
  'delete-history': 'Deletes the entire conversation history on transfer.'
};

const AssistantDestinationSettings = ({
  open,
  onOpenChange,
  settings,
  onSettingsChange,
  destinationName,
}: AssistantDestinationSettingsProps) => {
  const handleChange = (field: keyof DestinationSettings, value: string) => {
    onSettingsChange({
      ...settings,
      [field]: value,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Destination Settings for {destinationName}</DialogTitle>
          <DialogDescription>
            Configure how the conversation transfers to this destination assistant.
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid gap-6 py-4">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Label htmlFor="transferMode">Transfer Mode</Label>
              <HoverCard>
                <HoverCardTrigger>
                  <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                </HoverCardTrigger>
                <HoverCardContent className="w-80">
                  <div className="space-y-2">
                    <h4 className="font-semibold">Transfer Modes</h4>
                    {Object.entries(transferModeInfo).map(([mode, info]) => (
                      <div key={mode} className="space-y-1">
                        <p className="text-sm font-medium">{mode}</p>
                        <p className="text-sm text-muted-foreground">{info}</p>
                      </div>
                    ))}
                  </div>
                </HoverCardContent>
              </HoverCard>
            </div>
            <Select
              value={settings.transferMode}
              onValueChange={(value) => handleChange('transferMode', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="rolling-history">Rolling History</SelectItem>
                <SelectItem value="swap-system-message-in-history">Swap System Message</SelectItem>
                <SelectItem value="delete-history">Delete History</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">Transfer Message</Label>
            <Input
              id="message"
              value={settings.message}
              onChange={(e) => handleChange('message', e.target.value)}
              placeholder="Message spoken to customer before transfer..."
            />
            <p className="text-sm text-muted-foreground">
              This message will be spoken to the customer before connecting them to the destination.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Transfer Description</Label>
            <Textarea
              id="description"
              value={settings.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Description of when and how to transfer..."
              className="min-h-[100px]"
            />
            <p className="text-sm text-muted-foreground">
              This description helps the AI understand when and how to transfer the call.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AssistantDestinationSettings;
